import { pickBy, isNumber } from 'lodash-es';
import { CustomEventData } from '@slardar/sdk-web';
import BasePlugin from './plugins/base-plugin';
import BeforeSendStrategy from './default-before-send';
import { errorHandler } from '../error-handler';
import { TagsType, ErrorLevel, CaptureFunc, PromptParams } from '../types';
import {
  parseTags,
  prettyPrint,
  level2color,
  convertResponseInfo,
} from '../utils';
import type { BrowserCommandClient } from '@slardar/web';

export type LoggerConfig = {
  bid?: string;
  release?: string;
  env?: string;
  userId?: string | number;
  beforeSend?: (event: any) => void;
  plugins?: Record<string, any>;
  customModules?: (new () => BasePlugin)[];
  extra?: Record<string, string | number>;
};

class Logger {
  static BasePlugin: typeof BasePlugin = BasePlugin;

  static BeforeSendStrategy: typeof BeforeSendStrategy = BeforeSendStrategy;

  slardar: BrowserCommandClient;

  constructor(slardarInstance: BrowserCommandClient) {
    this.slardar = slardarInstance;
  }

  updateSlardarInstance(slardarInstance: BrowserCommandClient) {
    this.slardar = slardarInstance;
  }

  config(config: LoggerConfig) {
    try {
      const { beforeSend, ...restConfig } = config;
      this.slardar.config({
        ...restConfig,
        userId: config.userId as string,
      });
      if (config.extra) {
        Object.keys(config.extra).forEach(key => {
          if (config.extra?.[key] !== undefined) {
            this.slardar?.context?.set(key, config.extra?.[key]);
          }
        });
      }
    } catch (error) {
      console.error('slardar config error: ', error);
    }
  }

  start = (config?: LoggerConfig) => {
    let beforeSendHandler;
    if (config) {
      const { beforeSend } = config;
      beforeSendHandler = beforeSend;
      this.config(config);
    }
    if (!beforeSendHandler) {
      const defaultBeforeSendStrategy = new BeforeSendStrategy();
      defaultBeforeSendStrategy.init(config?.customModules || []);
      beforeSendHandler = defaultBeforeSendStrategy.beforeSend.bind(
        defaultBeforeSendStrategy,
      );
    }
    // before start, add before send hook
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore
    this.slardar.on?.('beforeSend', beforeSendHandler);
    setTimeout(() => {
      this.slardar('start');
    });
  };

  /**
   * send custom event info
   * @param name log name
   * @param tags key / value pair
   */
  info = (name: string, tags: TagsType = {}) => {
    const metrics = pickBy(tags, isNumber);
    const categories = parseTags(tags, true);

    try {
      this.slardar.sendEvent?.({
        name,
        metrics,
        categories,
      });
    } catch (e) {
      console.error('Slardar sendEvent', e);
    }

    // print local log for debugger
    prettyPrint({ label: 'info =>' }, name, metrics, categories);
  };

  /**
   * send custom event
   * @param params slardar params
   */
  custom = (params: CustomEventData) => {
    try {
      this.slardar.sendEvent?.(params);
    } catch (e) {
      console.error('Slardar sendEvent', e);
    }
    // print local log for debugger
    prettyPrint({ label: 'custom =>' }, params);
  };

  /**
   * send custom event info with counter
   * @param name log name
   * @param tags key / value pair
   */
  counter = (name: string, tags: TagsType = {}) =>
    this.info(name, { ...tags, counter: 1 });

  /**
   * send error info to js error
   * @param level error level， "fatal" | "error" | "warn"
   * @param label mark module info
   * @param description error description
   * @param error Error info
   * @param tags  extra info
   */
  capture = (
    level: ErrorLevel,
    label: string,
    description: string,
    error: Error = new Error(''),
    tags: TagsType = {},
    // eslint-disable-next-line max-params
  ) => {
    try {
      const detail = errorHandler(level, label, description, error, tags);
      const { reason, extra } = detail;

      if (detail.level === 'info') {
        this.info(reason, { ...extra, label, description });
      } else {
        const err = detail.error;
        const reasonStr = reason ? ` (${reason})` : '';
        if (detail.description) {
          err.message = `${detail.description}${reasonStr}`;
        } else {
          err.message = reason || '';
        }
        err.name = `[${detail.level}][${detail.label}] - ${err.name}`;

        this.slardar.captureException?.(err, extra);

        // print local log for debugger
        prettyPrint(
          { label: `${level} =>`, color: level2color(detail.level) },
          err.message,
          error,
          tags,
        );
      }
    } catch (e) {
      console.error('Slardar inner error', e);
    }
  };

  /**
   * fatal error, need to resolve immediately
   * @param label mark module info
   * @param description error info
   * @param error Error
   * @param tags  extra info
   */
  fatal: CaptureFunc = (
    label: string,
    description: string,
    err?: Error,
    tags?: TagsType,
  ) => this.capture('fatal', label, description, err, tags);

  /**
   * common error
   * @param label mark module info
   * @param description error info
   * @param error Error
   * @param tags  extra info
   */
  error: CaptureFunc = (
    label: string,
    description: string,
    err?: Error,
    tags?: TagsType,
  ) => this.capture('error', label, description, err, tags);

  /**
   * warning
   * @param label mark module info
   * @param description error info
   * @param error Error
   * @param tags  extra info
   */
  warn: CaptureFunc = (
    label: string,
    description: string,
    err?: Error,
    tags?: TagsType,
  ) => this.capture('warn', label, description, err, tags);

  /**
   * custom perf
   * @param key mark module info
   * @param value perf value
   * @param tags  extra info
   */
  perf = (key: string, value: number, tags?: TagsType) =>
    this.slardar?.sendCustomPerfMetric?.({
      name: key,
      value,
      type: tags?.type || 'perf',
      isCustom: true,
      extra: tags ? parseTags(tags) : undefined,
    });

  /**
   * use for page backup prmopt capture
   * @param params PromptParams
   */
  backupPrompt = (params: PromptParams) => {
    const { scene, promptMessage, errorMessage, responseInfo, extraInfo } =
      params;
    this.info('rd_ec_content_backup_prompt', {
      scene,
      promptMessage,
      errorMessage,
      ...convertResponseInfo(responseInfo),
      ...extraInfo,
    });
  };
}

export default Logger;
